import {DataStatus} from 'platform/components';

import {useRef} from 'react';

import {useCreateServiceVehicleV2Mutation} from '@dms/api/vehicle';
import {
  buildBatteryCertificateFileFields,
  ServiceVehicle,
  toAPIVehicle,
  useGetCarFeatures,
  useVehicleWarrantySync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {
  FullScreenModal,
  handleApiError,
  useMandatoryFields,
  VehicleCreationRequest,
} from '@dms/shared';

import {CREATE_SERVICE_VEHICLE_DEFAULT_VALUES} from '../constants/CREATE_SERVICE_VEHICLE_DEFAULT_VALUES';
import {
  getServiceVehicleFieldAvailabilityContext,
  getServiceVehicleMandatorySchema,
} from '../utils/getServiceVehicleMandatorySchema';
import {ServiceVehicleForm} from './ServiceVehicleForm';

const MANDATORY_FIELDS_OPTIONS = {
  definitionId: 'SERVICE',
  requiredStageId: 'SERVICE_ON_CREATION',
};

interface CreateServiceVehicleModalProps {
  prefill?: VehicleCreationRequest['prefill'];
  onCreated: (vehicleId: string) => void;
  onDiscard: () => void;
  isEmbedded?: boolean;
}

export function CreateServiceVehicleModal(props: CreateServiceVehicleModalProps) {
  const [createServiceVehicleMutation, {isLoading: isCreating}] =
    useCreateServiceVehicleV2Mutation();
  const [getCarFeatures] = useGetCarFeatures();
  const {syncWarranties, isSyncing} = useVehicleWarrantySync();
  // isCreating only covers the mutation, leaving the button live across the car-features await.
  const isSubmitting = useRef(false);
  const isDiscarded = useRef(false);

  const handleDiscard = () => {
    isDiscarded.current = true;
    props.onDiscard();
  };

  const {
    generateSchema,
    isLoading: isMandatoryFieldsLoading,
    isError: isMandatoryFieldsError,
    isMandatoryFieldsEnabled,
  } = useMandatoryFields(MANDATORY_FIELDS_OPTIONS);
  const formSchema = getServiceVehicleMandatorySchema(generateSchema);

  const handleSave = async (values: ServiceVehicle) => {
    if (isSubmitting.current) {
      return;
    }
    isSubmitting.current = true;

    try {
      const sourcingData = await getCarFeatures(values);
      const apiPayload = toAPIVehicle(values, null, sourcingData);

      await createServiceVehicleMutation({
        createServiceVehicleV2RequestBody: {
          vehicle: apiPayload,
          service: {vehicleClassId: values.vehicleClassId},
          additionalNote: values.additionalNote,
        },
        validateMandatoryFields: isMandatoryFieldsEnabled(
          undefined,
          getServiceVehicleFieldAvailabilityContext(values)
        ),
        fileFields: buildBatteryCertificateFileFields(values.batteryCertificateDocument),
      })
        .unwrap()
        .then(async (createdVehicle) => {
          await syncWarranties({
            vehicleId: createdVehicle.vehicleId,
            warranties: values.warranties ?? [],
          });

          // The save outlives the form: Discard stays live across the getCarFeatures await, and
          // calling back after that would assign a vehicle the user had already dismissed.
          if (isDiscarded.current) {
            return;
          }

          props.onCreated(createdVehicle.vehicleId);
        })
        .catch(handleApiError);
    } finally {
      isSubmitting.current = false;
    }
  };

  // DataStatus replaces its children, so these states need their own shell - bare, they would
  // drop a loose spinner into the host's layout and show no modal at all.
  if (isMandatoryFieldsLoading || isMandatoryFieldsError) {
    return (
      <FullScreenModal
        headline={i18n.t('entity.vehicle.labels.createServiceVehicle')}
        onClose={props.onDiscard}
      >
        <DataStatus
          isLoading={isMandatoryFieldsLoading}
          isError={isMandatoryFieldsError}
          minHeight={100}
        />
      </FullScreenModal>
    );
  }

  return (
    <VFKit.Form
      showMandatoryFields
      mandatoryFieldsOptions={MANDATORY_FIELDS_OPTIONS}
      mode="onBlur"
      experimentalZodSchema={formSchema}
      defaultValues={{...CREATE_SERVICE_VEHICLE_DEFAULT_VALUES, ...props.prefill}}
    >
      <ServiceVehicleForm
        onSave={handleSave}
        onDiscard={handleDiscard}
        isCreating={isCreating || isSyncing}
        isEmbedded={props.isEmbedded}
        shouldShowWarranties
      />
    </VFKit.Form>
  );
}
